import {UI} from "../../../../../../stem-core/src/ui/UIBase";

import {styleRule, StyleSheet} from "../../../../../../stem-core/src/ui/Style";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {countriesDataService} from "../../../../../services/CountriesDataService";
import {Messages} from "../../../../../Messages";
import {NOOP_FUNCTION} from "../../../../../../stem-core/src/base/Utils";
import {SUBSCRIBE_PLAN_STATE, subscribePlanController} from "../../../../inline-subscription/SubscribePlanController";
import {subscribeFlowState} from "../../../../inline-subscription/SubscribeFlowState";
import {LoadingSpinner} from "../../../../../ui/LoadingSpinner";
import {Money} from "../../../../../../client/state/misc/Money";
import {isSmallScreen} from "../../../../../UtilsLib";
import {SubscribeButton} from "./SubscribeButton";
import {INLINE_EDIT_SUBSCRIPTION_URLS} from "../../../../PaywallConstants";
import {Router} from "../../../../../../stem-core/src/ui/Router";
import {BUTTON_TYPE} from "../../../../../Constants";


class TNRSubscriptionCoverageCardStyle extends StyleSheet {
    @styleRule
    coverageCard = {
        minHeight: () => isSmallScreen() ? "" : "484px",
        padding: "18px 24px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        background: this.themeProps.WHITE,
        border: "1px solid #DDDDDD",
        width: () => isSmallScreen() ? 344 : 418,
    };

    @styleRule
    bestValueHeader = {
        textAlign: "center",
        width: "100%",
        padding: 10,
        top: 0,
        left: 0,
        position: "absolute",
        background: "#D6D6D6",
        textTransform: "uppercase",
    };

    @styleRule
    bestValueHeaderGuard = {
        height: 42,
    };

    @styleRule
    bestValueCard = {
        boxShadow: "inset 0 0 0 4px #DDDDDD",
    };

    @styleRule
    title = {
        fontSize: 24,
        fontWeight: 600,
    };

    @styleRule
    cycleDuration = {
        fontSize: 20,
        marginBottom: 18,
    };

    @styleRule
    pricesBreakdown = {
        width: "100%",
        padding: "12px 6px",
        borderTop: () => "1px solid #E5E5E5",
        borderBottom: () => "1px solid #E5E5E5",
        display: "flex",
        justifyContent: "space-between",
        fontSize: 18,
    };

    @styleRule
    pricesBreakdownEntry = {
        padding: 6,
        ":first-child": {
            paddingLeft: 0,
        },
        ":last-child": {
            paddingRight: 0,
        }
    };

    @styleRule
    perkText = {
        fontSize: 16,
        marginTop: 12,
    };

    @styleRule
    perkSubtext = {
        marginLeft: 24,
    };

    @styleRule
    perk = {
        marginTop: 8,
        ":last-child": {
            marginBottom: 18,
        }
    };

    @styleRule
    button = {
        width: "100%",
        bottom: 24,
    };

    @styleRule
    subscriptionPerksList = {
        marginTop: 18,
        marginBottom: -4,
        fontSize: 16,
    };
}

// TODO: Extend SubscriptionCoverageCard at this point.
@registerStyle(TNRSubscriptionCoverageCardStyle)
export class TNRSubscriptionCoverageCard extends UI.Element {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            offer: null,
            bestValue: false,
            onSubscriptionOfferSelected: NOOP_FUNCTION,
            testId: "subscriptionCoverageCard",
        };
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.coverageCard);
        if (this.options.bestValue) {
            attr.addClass(this.styleSheet.bestValueCard);
        }
    }

    getOfferLabel() {
        if (subscribePlanController.subscribePlanState === SUBSCRIBE_PLAN_STATE.subscribe) {
            return Messages.subscribe;
        }
        return Messages.changeSubscription;
    }

    userHasEquivalentSubscription(offer) {
        if (subscribePlanController.subscribePlanState === SUBSCRIBE_PLAN_STATE.subscribe) {
            return false;
        }
        return subscribeFlowState.userHasEquivalentSubscriptionToOffer(offer);
    }

    getPricesBreakdown(offer) {
        if (!offer.shouldCollectAddress() || !offer.hasShippingPrice()) {
            return [[
                offer.getName(),
                offer.getPrice(),
            ]];
        }

        return offer.billingPlan.shippingPrices.map(({countryCode, amount}) => [
            countriesDataService.getCountryByCode(countryCode)?.name || Messages.international,
            offer.getPrice().add(new Money(amount, offer.getCurrency()))
        ])
    }

    render() {
        const {offer, onSubscriptionOfferSelected, bestValue} = this.options;
        const coverage = offer.getCoverage();
        const {styleSheet} = this;

        const button = this.userHasEquivalentSubscription(offer) ?
            <SubscribeButton onClick={() => Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancel)} type={BUTTON_TYPE.SECONDARY}>
                {Messages.cancelSubscription}
            </SubscribeButton> :
            <SubscribeButton className={styleSheet.button} onClick={() => onSubscriptionOfferSelected(offer)}>
                {this.getOfferLabel()}
            </SubscribeButton>;

        return [
            bestValue ? [
                <div className={styleSheet.bestValueHeader}>
                    {Messages.bestValue}
                </div>,
                <div className={styleSheet.bestValueHeaderGuard}/>
            ] : null,
            <div className={styleSheet.title}>{coverage.getName()}</div>,
            <div className={styleSheet.cycleDuration}>{offer.billingPlan.getCycleDuration()}</div>,
            <div className={styleSheet.pricesBreakdown}>
                {this.getPricesBreakdown(offer).map(([offerName, price]) =>
                    <div className={styleSheet.pricesBreakdownEntry}>
                        <div>{offerName === "Digital" ? "Worldwide" : offerName}</div>
                        <div>{price}</div>
                    </div>)}
            </div>,
            <div className={styleSheet.subscriptionPerksList}>{Messages.subscriptionPerksList}</div>,
            <div>
                {(coverage.getDescription().items || []).map(({text, subtext}) =>
                    <div className={styleSheet.perk}>
                        <li className={styleSheet.perkText}>{text}</li>
                        <div className={styleSheet.perkSubtext}>{subtext}</div>
                    </div>
                )}
            </div>,
            <div style={{flex: 1}}/>,
            button
        ];
    }

    onMount() {
        super.onMount();

        LoadingSpinner.show();
        countriesDataService.loadCountriesData(() => {
            LoadingSpinner.hide();
            this.redraw();
        });
    }
}
